import {
  MaterialType,
  StudyContentPageType,
  TotalCourse,
} from '@asset/enum/materialEnum';
import { StudyStep } from '@asset/enum/problemSolvingEnum';
import { TeacherPage } from '@asset/enum/page';
import { UrlObject } from 'url';
import qs from 'qs';
import { ParsedUrlQuery, ParsedUrlQueryInput } from 'querystring';
import {
  FeedbackListColumn,
  FeedbackListFilter,
  FeedbackListSort,
} from '@asset/enum/feedback';
import _ from 'lodash';

export const STUDENT_STUDY_CONTENT_URL_PREFIX =
  '/manage/student-study-content/';
export const MY_STUDY_CONTENT_URL_PREFIX = '/my-study-content/';
export const ASSIGNMENT_RESULT_URL_PREFIX = '/assignment-result/';
export const STUDY_REPORT_ASSIGNMENT_RESULT_URL_PREFIX =
  '/study-report/assignment-result/';
export const STUDY_REPORT_ASSIGNMENT_RESULT_DETAIL_URL_PREFIX =
  '/study-report/assignment-result/detail/';
export const MANAGE_FEEDBACK_URL_PREFIX = `/manage/dashboard/${TeacherPage.FEEDBACKS}/`;
export const MANAGE_ROADMAP_URL_PREFIX = `/manage/dashboard/${TeacherPage.ROADMAP}/`;

export const getManageStudentStudyContentUrlObject = (
  studyContentType: StudyContentPageType,
  studyContentId: number,
  query?: {
    studentId?: number;
    editingAssignmentId?: number;
    modalType?: 'studentAnswer' | 'material';
    pageId?: number;
    problemStatusId?: number;
    problemStatusDetailId?: number;
    pagesIndex?: number;
  } | null,
): UrlObject => {
  const urlString = `${STUDENT_STUDY_CONTENT_URL_PREFIX}${studyContentType}/${studyContentId}`;
  return { pathname: urlString, query: query };
};

export const getProblemSolvingUrlObject = (
  studyContentId: number,
  studyContentType: StudyContentPageType,
  pageId: number,
  query?: {
    type?: StudyStep;
    previousPage?: string;
    reportId?: number;
    problemFiltering?: string;
    problemStatusId?: number;
  } | null,
): UrlObject => {
  const urlString = `/problem-solving/${studyContentId}/${studyContentType}/${pageId}`;
  return { pathname: urlString, query: query };
};

export const getMyStudyContentUrlObject = (
  studyContentType: StudyContentPageType,
  studyContentId: number,
  query?: {
    problemFiltering?: string;
    limit?: number;
    offset?: number;
  } | null,
): UrlObject => {
  const urlString = `${MY_STUDY_CONTENT_URL_PREFIX}${studyContentType}/${studyContentId}`;
  return { pathname: urlString, query: query };
};

export const getManageFeedbackUrlObject = (
  studentId: number,
  query: {
    type?: string[];
    [FeedbackListColumn.IS_CHECK]?: boolean[];
    [FeedbackListColumn.SOLVED_AT]?: ('desc' | 'asc')[];
    [FeedbackListColumn.MATERIAL_COURSE]?: TotalCourse[];
    [FeedbackListColumn.MATERIAL_TYPE]?: MaterialType[];
  } = {},
): UrlObject => {
  const defaultQuery = {
    [FeedbackListColumn.IS_CHECK]: false,
  };
  const urlString = `${MANAGE_FEEDBACK_URL_PREFIX}${studentId}`;
  delete query?.type;
  let _query: ParsedUrlQueryInput = query;
  if (_.isEmpty(query) || _.isNil(query)) {
    _query = defaultQuery;
  }
  return { pathname: urlString, query: _query };
};

export const getManageSchedulesUrlObject = (
  studentId: number,
  query: {
    type?: string[];
    startDay?: string;
  } = {},
): UrlObject => {
  const urlString = `/manage/dashboard/${TeacherPage.SCHEDULE}/${studentId}`;
  delete query?.type;
  return { pathname: urlString, query: query };
};

export const getManageRoadmapUrlObject = (studentId: number): UrlObject => {
  const urlString = `${MANAGE_ROADMAP_URL_PREFIX}${studentId}`;
  return { pathname: urlString };
};

export const getAssignmentResultUrlObject = (
  assignmentId: number,
  query?: {
    nextMissionId?: number;
    historyBack?: boolean;
  } | null,
): UrlObject => {
  const urlString = `${ASSIGNMENT_RESULT_URL_PREFIX}${assignmentId}`;
  return { pathname: urlString, query: query };
};

export const getStudyReportAssignmentResultDetailUrlObject = (
  reportId: string,
  query: {
    checkedIdData: string;
  },
): UrlObject => {
  const urlString = `${STUDY_REPORT_ASSIGNMENT_RESULT_DETAIL_URL_PREFIX}${reportId}`;
  return { pathname: urlString, query };
};

export const STUDY_CONTENT_REPORT_URL_PREFIX = '/study-content-report/';

export const getStudyContentReportUrlObject = (
  studyContentIds: number | string[],
  query?: {
    checkedIdData: string;
    course?: string;
  },
): UrlObject => {
  const urlString = `${STUDY_CONTENT_REPORT_URL_PREFIX}${studyContentIds}`;
  return { pathname: urlString, query };
};

export const getTestCoverUrlObject = (
  contentId: number,
  pageType: string,
): UrlObject => {
  const urlString = `/test/cover/${contentId}/${pageType}`;
  return { pathname: urlString };
};

export const getFeedbackDetailUrlObject = (query: {
  pageId: number;
  problemStatusDetailId: number;
  [FeedbackListFilter.IS_CHECK]?: boolean[];
  [FeedbackListSort.SOLVED_AT]?: ('desc' | 'asc')[];
  [FeedbackListFilter.MATERIAL_COURSE]?: TotalCourse[];
  [FeedbackListFilter.MATERIAL_TYPE]?: MaterialType[];
}): UrlObject => {
  const urlString = `/feedback/detail`;
  const filteredString = qs.stringify(query, {
    arrayFormat: 'comma',
    skipNulls: true,
  });
  const filteredQueryObject = qs.parse(filteredString);
  return { pathname: urlString, query: filteredQueryObject as ParsedUrlQuery };
};
